import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { type FC, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

type TBaseProps = Omit<TextFieldProps, 'name'> & {
  name: string;
  options?: string[];
};

export const RHFChipInput: FC<TBaseProps> = ({ name, helperText, label, placeholder, options = [], ...rest }) => {
  const { control, setValue } = useFormContext();
  const [inputValue, setInputValue] = useState('');

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const value: string[] = Array.isArray(field.value) ? field.value : [];

        return (
          <Autocomplete
            multiple
            freeSolo
            options={options}
            value={value}
            inputValue={inputValue}
            onInputChange={(_, val) => setInputValue(val)}
            onChange={(_, newValue) => {
              const next = [
                ...new Set((newValue as string[]).map(v => (typeof v === 'string' ? v.trim() : v)).filter(Boolean)),
              ];
              field.onChange(next);
              setValue(name, next, { shouldDirty: true, shouldValidate: true });
            }}
            fullWidth
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const tagProps = getTagProps({ index });
                // oxlint-disable-next-line eslint-plugin-react(jsx-key)
                return <Chip variant="outlined" label={option} size="small" {...tagProps} key={tagProps.key} />;
              })
            }
            renderInput={params => (
              <TextField
                {...params}
                {...rest}
                label={label}
                placeholder={value.length === 0 ? placeholder : undefined}
                error={!!error}
                helperText={error ? error.message : (helperText ?? 'Press Enter or Tab to add')}
                onKeyDown={e => {
                  if (e.key === 'Tab') {
                    const input = inputValue.trim();
                    if (!input) {
                      return;
                    }
                    e.preventDefault();
                    const next = [...new Set([...value, input])];
                    field.onChange(next);
                    setValue(name, next, { shouldDirty: true, shouldValidate: true });
                    setInputValue('');
                  }
                }}
                onBlur={() => {
                  const input = inputValue.trim();
                  if (!input) {
                    return;
                  }
                  const next = [...new Set([...value, input])];
                  field.onChange(next);
                  setValue(name, next, { shouldDirty: true, shouldValidate: true });
                  setInputValue('');
                }}
              />
            )}
          />
        );
      }}
    />
  );
};
